import React, { useState, useEffect } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Edit2, Calendar as CalendarIcon, Send, Check, Eye, CreditCard, Loader2, Building2, Download } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

export function Invoices() {
  const { selectedEntity, isDutchEntity } = useEntity();
  const { t, language } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    client_name: '',
    client_address: '',
    client_email: '',
    issue_date: new Date(),
    due_date: addDays(new Date(), 30),
    items: [{ description: '', quantity: 1, amount: 0 }],
    vat_rate: isDutchEntity ? 21 : 0,
    notes: '',
    payment_methods: ['bank_transfer']  // bank_transfer, stripe
  });

  useEffect(() => {
    if (selectedEntity) {
      fetchInvoices();
    }
  }, [selectedEntity]);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      // Poll for payment status
      pollPaymentStatus(sessionId);
      // Clear URL params
      setSearchParams({});
    } else if (paymentStatus === 'cancelled') {
      toast.error(isDutchEntity && language === 'nl' ? 'Betaling geannuleerd' : 'Payment cancelled');
      setSearchParams({});
    }
  }, [searchParams]); // eslint-disable-line

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error(isDutchEntity && language === 'nl' 
        ? 'Kon betalingsstatus niet verifiëren' 
        : 'Could not verify payment status');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/payments/status/${sessionId}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          toast.success(isDutchEntity && language === 'nl' 
            ? 'Betaling succesvol!' 
            : 'Payment successful!');
          fetchInvoices();
          return;
        } else if (data.status === 'expired') {
          toast.error(isDutchEntity && language === 'nl' 
            ? 'Betalingssessie verlopen' 
            : 'Payment session expired');
          return;
        }
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status check error:', error);
    }
  };

  const initiatePayment = async (invoiceId) => {
    setProcessingPayment(invoiceId);
    
    try {
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invoice_id: invoiceId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Payment error');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error(isDutchEntity && language === 'nl' 
        ? 'Kon betaling niet starten' 
        : 'Could not initiate payment');
    } finally {
      setProcessingPayment(null);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/invoices?entity_id=${selectedEntity.entity_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_URL}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entity_id: selectedEntity.entity_id,
          client_name: formData.client_name,
          client_address: formData.client_address,
          client_email: formData.client_email,
          issue_date: formData.issue_date.toISOString(),
          due_date: formData.due_date.toISOString(),
          items: formData.items,
          vat_rate: formData.vat_rate || null,
          currency: isDutchEntity ? 'EUR' : 'USD',
          notes: formData.notes,
          payment_methods: formData.payment_methods
        })
      });

      if (response.ok) {
        fetchInvoices();
        resetForm();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to create invoice:', error);
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      const response = await fetch(
        `${API_URL}/api/invoices/${invoiceId}/status?status=${status}`,
        { method: 'PUT', credentials: 'include' }
      );
      if (response.ok) {
        fetchInvoices();
      }
    } catch (error) {
      console.error('Failed to update invoice status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_address: '',
      client_email: '',
      issue_date: new Date(),
      due_date: addDays(new Date(), 30),
      items: [{ description: '', quantity: 1, amount: 0 }],
      vat_rate: isDutchEntity ? 21 : 0,
      notes: '',
      payment_methods: ['bank_transfer']
    });
  };

  const togglePaymentMethod = (method) => {
    const currentMethods = formData.payment_methods || [];
    if (currentMethods.includes(method)) {
      // Don't allow removing the last method
      if (currentMethods.length > 1) {
        setFormData({
          ...formData,
          payment_methods: currentMethods.filter(m => m !== method)
        });
      }
    } else {
      setFormData({
        ...formData,
        payment_methods: [...currentMethods, method]
      });
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, amount: 0 }]
    });
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...formData.items];
    newItems[idx][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (idx) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== idx)
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(isDutchEntity ? 'nl-NL' : 'en-US', {
      style: 'currency',
      currency: isDutchEntity ? 'EUR' : 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="badge-info">{isDutchEntity && language === 'nl' ? 'Verzonden' : 'Sent'}</Badge>;
      case 'paid':
        return <Badge className="badge-success">{isDutchEntity && language === 'nl' ? 'Betaald' : 'Paid'}</Badge>;
      case 'overdue':
        return <Badge className="badge-error">{isDutchEntity && language === 'nl' ? 'Achterstallig' : 'Overdue'}</Badge>;
      default:
        return <Badge className="badge-warning">{isDutchEntity && language === 'nl' ? 'Concept' : 'Draft'}</Badge>;
    }
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0) * (parseInt(item.quantity) || 1), 0);
  };

  if (!selectedEntity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {isDutchEntity ? 'Selecteer eerst een entiteit' : 'Select an entity first'}
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.invoices')}
          </h1>
          <p className="text-muted-foreground">{selectedEntity.name}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-brand" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('invoices.newInvoice')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('invoices.newInvoice')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Client Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('invoices.client')}</Label>
                  <Input
                    data-testid="client-name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    data-testid="client-email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>{isDutchEntity && language === 'nl' ? 'Adres' : 'Address'}</Label>
                <Textarea
                  data-testid="client-address"
                  value={formData.client_address}
                  onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Dates */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('invoices.issueDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.issue_date, 'PPP', { locale: isDutchEntity && language === 'nl' ? nl : enUS })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.issue_date}
                        onSelect={(date) => date && setFormData({ ...formData, issue_date: date })}
                        locale={isDutchEntity && language === 'nl' ? nl : enUS}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>{t('invoices.dueDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.due_date, 'PPP', { locale: isDutchEntity && language === 'nl' ? nl : enUS })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.due_date}
                        onSelect={(date) => date && setFormData({ ...formData, due_date: date })}
                        locale={isDutchEntity && language === 'nl' ? nl : enUS}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <Label>{t('invoices.items')}</Label>
                <div className="space-y-2 mt-2">
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <Input
                        className="flex-1"
                        placeholder={isDutchEntity && language === 'nl' ? 'Omschrijving' : 'Description'}
                        value={item.description}
                        onChange={(e) => updateItem(idx, 'description', e.target.value)}
                        data-testid={`item-${idx}-description`}
                      />
                      <Input
                        className="w-20"
                        type="number"
                        min="1"
                        placeholder={isDutchEntity && language === 'nl' ? 'Aantal' : 'Qty'}
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                        data-testid={`item-${idx}-quantity`}
                      />
                      <Input
                        className="w-28"
                        type="number"
                        step="0.01"
                        placeholder={isDutchEntity && language === 'nl' ? 'Bedrag' : 'Amount'}
                        value={item.amount}
                        onChange={(e) => updateItem(idx, 'amount', e.target.value)}
                        data-testid={`item-${idx}-amount`}
                      />
                      {formData.items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    {t('invoices.addItem')}
                  </Button>
                </div>
              </div>

              {/* VAT */}
              {isDutchEntity && (
                <div>
                  <Label>{isDutchEntity && language === 'nl' ? 'BTW Tarief (%)' : 'VAT Rate (%)'}</Label>
                  <Select
                    value={formData.vat_rate.toString()}
                    onValueChange={(v) => setFormData({ ...formData, vat_rate: parseInt(v) })}
                  >
                    <SelectTrigger className="w-32" data-testid="vat-rate-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="9">9%</SelectItem>
                      <SelectItem value="21">21%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label>{isDutchEntity && language === 'nl' ? 'Opmerkingen' : 'Notes'}</Label>
                <Textarea
                  data-testid="invoice-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Payment Methods */}
              <div>
                <Label>{isDutchEntity && language === 'nl' ? 'Betaalmethodes' : 'Payment Methods'}</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  {isDutchEntity && language === 'nl' 
                    ? 'Kies welke betaalmethodes beschikbaar zijn voor deze factuur'
                    : 'Choose which payment methods are available for this invoice'}
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="pm-bank"
                      data-testid="pm-bank-transfer"
                      checked={formData.payment_methods?.includes('bank_transfer')}
                      onCheckedChange={() => togglePaymentMethod('bank_transfer')}
                    />
                    <label htmlFor="pm-bank" className="flex items-center gap-2 text-sm cursor-pointer">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      {isDutchEntity && language === 'nl' ? 'Bankoverschrijving' : 'Bank Transfer'}
                      <span className="text-xs text-muted-foreground">
                        ({isDutchEntity ? 'IBAN' : 'Wire Transfer'})
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="pm-stripe"
                      data-testid="pm-stripe"
                      checked={formData.payment_methods?.includes('stripe')}
                      onCheckedChange={() => togglePaymentMethod('stripe')}
                    />
                    <label htmlFor="pm-stripe" className="flex items-center gap-2 text-sm cursor-pointer">
                      <CreditCard className="w-4 h-4 text-muted-foreground" />
                      {isDutchEntity && language === 'nl' ? 'Online betalen (Stripe)' : 'Pay Online (Stripe)'}
                      <span className="text-xs text-muted-foreground">
                        ({isDutchEntity && language === 'nl' ? 'iDEAL, creditcard' : 'Card, ACH'})
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Totals Preview */}
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('common.subtotal')}</span>
                  <span>{formatCurrency(calculateSubtotal())}</span>
                </div>
                {isDutchEntity && formData.vat_rate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>BTW ({formData.vat_rate}%)</span>
                    <span>{formatCurrency(calculateSubtotal() * formData.vat_rate / 100)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t border-border pt-2">
                  <span>{t('common.total')}</span>
                  <span>{formatCurrency(calculateSubtotal() * (1 + (isDutchEntity ? formData.vat_rate : 0) / 100))}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="flex-1 btn-brand" data-testid="save-invoice-btn">
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="py-12 text-center">
          <div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : invoices.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('invoices.invoiceNumber')}</th>
                    <th>{t('invoices.client')}</th>
                    <th>{t('invoices.issueDate')}</th>
                    <th>{t('invoices.dueDate')}</th>
                    <th className="text-right">{t('common.total')}</th>
                    <th>{t('common.status')}</th>
                    <th className="w-32">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.invoice_id}>
                      <td className="font-mono text-sm">{inv.invoice_number}</td>
                      <td>{inv.client_name}</td>
                      <td>{new Date(inv.issue_date).toLocaleDateString(isDutchEntity ? 'nl-NL' : 'en-US')}</td>
                      <td>{new Date(inv.due_date).toLocaleDateString(isDutchEntity ? 'nl-NL' : 'en-US')}</td>
                      <td className="text-right font-mono">{formatCurrency(inv.total)}</td>
                      <td>{getStatusBadge(inv.status)}</td>
                      <td>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewInvoice(inv)}
                            data-testid={`preview-${inv.invoice_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/invoices/${inv.invoice_id}/pdf`, { credentials: 'include' });
                                if (!res.ok) throw new Error();
                                const blob = await res.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a'); a.href = url;
                                a.download = `Factuur_${inv.invoice_number || inv.invoice_id}.pdf`;
                                a.click(); window.URL.revokeObjectURL(url);
                              } catch { toast.error('PDF download mislukt'); }
                            }}
                            data-testid={`pdf-${inv.invoice_id}`}
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {inv.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateInvoiceStatus(inv.invoice_id, 'sent')}
                              data-testid={`send-${inv.invoice_id}`}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {(inv.status === 'sent' || inv.status === 'overdue') && (
                            <>
                              {inv.payment_methods?.includes('stripe') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => initiatePayment(inv.invoice_id)}
                                  disabled={processingPayment === inv.invoice_id}
                                  data-testid={`pay-${inv.invoice_id}`}
                                  title={isDutchEntity && language === 'nl' ? 'Betalen via Stripe' : 'Pay with Stripe'}
                                >
                                  {processingPayment === inv.invoice_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CreditCard className="w-4 h-4 text-[hsl(var(--brand))]" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateInvoiceStatus(inv.invoice_id, 'paid')}
                                data-testid={`manual-paid-${inv.invoice_id}`}
                                title={isDutchEntity && language === 'nl' ? 'Handmatig markeren als betaald' : 'Mark as paid manually'}
                              >
                                <Check className="w-4 h-4 text-emerald-600" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {isDutchEntity && language === 'nl'
                ? 'Nog geen facturen. Maak je eerste factuur aan!'
                : 'No invoices yet. Create your first invoice!'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Invoice Preview Dialog */}
      {previewInvoice && (
        <Dialog open={!!previewInvoice} onOpenChange={() => setPreviewInvoice(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{previewInvoice.invoice_number}</DialogTitle>
            </DialogHeader>
            <div className="invoice-preview">
              <div className="invoice-header">
                <div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {selectedEntity.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">{selectedEntity.address}</p>
                </div>
                <div className="invoice-details">
                  <p className="text-xl font-semibold">{isDutchEntity && language === 'nl' ? 'FACTUUR' : 'INVOICE'}</p>
                  <p className="text-sm">{previewInvoice.invoice_number}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-muted-foreground">{isDutchEntity && language === 'nl' ? 'Aan:' : 'Bill to:'}</p>
                <p className="font-medium">{previewInvoice.client_name}</p>
                <p className="text-sm whitespace-pre-line">{previewInvoice.client_address}</p>
              </div>

              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>{t('common.description')}</th>
                    <th className="text-right">{isDutchEntity && language === 'nl' ? 'Aantal' : 'Qty'}</th>
                    <th className="text-right">{isDutchEntity && language === 'nl' ? 'Prijs' : 'Price'}</th>
                    <th className="text-right">{t('common.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {previewInvoice.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.description}</td>
                      <td className="text-right">{item.quantity}</td>
                      <td className="text-right">{formatCurrency(item.amount)}</td>
                      <td className="text-right">{formatCurrency(item.amount * item.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="invoice-totals">
                <div className="flex justify-between w-48">
                  <span>{t('common.subtotal')}</span>
                  <span>{formatCurrency(previewInvoice.subtotal)}</span>
                </div>
                {previewInvoice.vat_rate && (
                  <div className="flex justify-between w-48">
                    <span>BTW ({previewInvoice.vat_rate}%)</span>
                    <span>{formatCurrency(previewInvoice.vat_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between w-48 font-bold border-t pt-2">
                  <span>{t('common.total')}</span>
                  <span>{formatCurrency(previewInvoice.total)}</span>
                </div>
              </div>

              {previewInvoice.notes && (
                <div className="mt-8 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">{isDutchEntity && language === 'nl' ? 'Opmerkingen:' : 'Notes:'}</p>
                  <p className="text-sm">{previewInvoice.notes}</p>
                </div>
              )}

              {/* Payment Instructions */}
              {previewInvoice.payment_methods?.includes('bank_transfer') && (
                <div className="mt-8 pt-4 border-t bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm mb-3">
                    {isDutchEntity && language === 'nl' ? 'Betalingsinstructies' : 'Payment Instructions'}
                  </h4>
                  
                  {isDutchEntity ? (
                    // Dutch bank transfer instructions
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">
                          {language === 'nl' ? 'Bank' : 'Bank'}:
                        </span>
                        <span className="font-medium">{selectedEntity.bank_name || 'ABN AMRO'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">IBAN:</span>
                        <span className="font-mono font-medium">{selectedEntity.bank_account || '-'}</span>
                      </div>
                      {selectedEntity.bank_swift && (
                        <div className="grid grid-cols-2 gap-2">
                          <span className="text-muted-foreground">BIC:</span>
                          <span className="font-mono font-medium">{selectedEntity.bank_swift}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">
                          {language === 'nl' ? 'Ten name van' : 'Account name'}:
                        </span>
                        <span className="font-medium">{selectedEntity.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">
                          {language === 'nl' ? 'Betalingskenmerk' : 'Reference'}:
                        </span>
                        <span className="font-mono font-medium text-[hsl(var(--brand))]">
                          {previewInvoice.payment_reference || previewInvoice.invoice_number}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 italic">
                        {language === 'nl' 
                          ? 'Vermeld altijd het betalingskenmerk bij uw overboeking.'
                          : 'Always include the payment reference with your transfer.'}
                      </p>
                    </div>
                  ) : (
                    // US wire transfer instructions
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Bank:</span>
                        <span className="font-medium">{selectedEntity.bank_name || 'Bank of America'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Account Number:</span>
                        <span className="font-mono font-medium">{selectedEntity.bank_account || '-'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Routing Number (ABA):</span>
                        <span className="font-mono font-medium">{selectedEntity.bank_routing || '-'}</span>
                      </div>
                      {selectedEntity.bank_swift && (
                        <div className="grid grid-cols-2 gap-2">
                          <span className="text-muted-foreground">SWIFT Code:</span>
                          <span className="font-mono font-medium">{selectedEntity.bank_swift}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Beneficiary:</span>
                        <span className="font-medium">{selectedEntity.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Reference:</span>
                        <span className="font-mono font-medium text-[hsl(var(--brand))]">
                          {previewInvoice.payment_reference || previewInvoice.invoice_number}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 italic">
                        Please include the reference number with your wire transfer.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Payment button for sent/overdue invoices with Stripe enabled */}
              {(previewInvoice.status === 'sent' || previewInvoice.status === 'overdue') && 
               previewInvoice.payment_methods?.includes('stripe') && (
                <div className="mt-8 pt-4 border-t">
                  <Button
                    onClick={() => {
                      setPreviewInvoice(null);
                      initiatePayment(previewInvoice.invoice_id);
                    }}
                    disabled={processingPayment === previewInvoice.invoice_id}
                    className="w-full btn-brand"
                    data-testid="preview-pay-btn"
                  >
                    {processingPayment === previewInvoice.invoice_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    {isDutchEntity && language === 'nl' ? 'Betalen via Stripe' : 'Pay with Stripe'}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
